import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { formatDistanceToNow } from "date-fns";
import {
  Box,
  Typography,
  Paper,
  List,
  CircularProgress,
  Pagination,
  Divider,
  Button,
  Stack,
  IconButton,
  Chip,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Badge,
  Drawer,
} from "@mui/material";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AirportShuttleIcon from "@mui/icons-material/AirportShuttle";
import FlightIcon from "@mui/icons-material/Flight";
import EmailIcon from "@mui/icons-material/Email";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonIcon from "@mui/icons-material/Person";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import {
  fetchNotificationsRequest,
  markNotificationReadRequest,
} from "../../redux/notifications/actions";
import { useNavigate } from "react-router-dom";

const NotificationsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    notifications,
    isLoading,
    totalRecords,
    currentPage,
    totalCount,
    totalPages,
    totalunreadcount,
  } = useSelector((state) => state.notification);
  const user = useSelector((state) => state.auth.user);

  const [pageSize, setPageSize] = useState(10);
  const [sortedNotifications, setSortedNotifications] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);

  // Initial load only
  useEffect(() => {
    if (isInitialLoad) {
      dispatch(
        fetchNotificationsRequest({
          pageNo: currentPage,
          pageSize,
          paginationRequired: true,
        })
      );
      setIsInitialLoad(false);
    }
  }, [dispatch, isInitialLoad, currentPage, pageSize]);

  useEffect(() => {
    if (notifications && notifications.length > 0) {
      const sorted = [...notifications].sort((a, b) => {
        const aIsRead = a.is_readed || a.is_read;
        const bIsRead = b.is_readed || b.is_read;
        if (!aIsRead && bIsRead) return -1;
        if (aIsRead && !bIsRead) return 1;

        return new Date(b.created_at) - new Date(a.created_at);
      });

      setSortedNotifications(sorted);
    } else {
      setSortedNotifications([]);
    }
  }, [notifications]);

  const handlePageChange = (event, value) => {
    // Only dispatch if the page actually changed
    if (value !== currentPage) {
      dispatch(
        fetchNotificationsRequest({
          pageNo: value,
          pageSize,
          paginationRequired: true,
        })
      );
    }
  };

  const handlePageSizeChange = (event) => {
    const newPageSize = event.target.value;
    setPageSize(newPageSize);
    dispatch(
      fetchNotificationsRequest({
        pageNo: 1,
        pageSize: newPageSize,
        paginationRequired: true,
      })
    );
  };

  const handleViewNotification = (notification) => {
    setSelectedNotification(notification);
    setIsDetailDrawerOpen(true);

    const notificationId =
      notification.id || notification.admin_notification_id;
    if (!notification.is_readed && !notification.is_read) {
      dispatch(markNotificationReadRequest(notificationId));
    }
  };

  const handleCloseDetailDrawer = () => {
    setIsDetailDrawerOpen(false);
    setSelectedNotification(null);
  };

  // Add function to handle airport navigation
  const handleNavigateToAirports = (notification) => {
    // Mark as read if not already read
    const notificationId =
      notification.id || notification.admin_notification_id;
    if (!notification.is_readed && !notification.is_read) {
      dispatch(markNotificationReadRequest(notificationId));
    }

    // Navigate to airports pending screen
    navigate("/airports"); // Adjust the route as per your routing structure
  };

  // Add function to check if notification is airport update
  const isAirportUpdateNotification = (notification) => {
    return (
      notification.booking_message === "New airport update pending approval" ||
      notification.message === "New airport update pending approval"
    );
  };

  const formatDate = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch (error) {
      return dateString;
    }
  };

  const extractBookingReference = (message) => {
    const match = message?.match(/booking reference number: ([A-Z0-9]+)/);
    return match ? match[1] : "";
  };

  const extractServiceType = (message) => {
    const match = message?.match(/Your ([^-]+) service/);
    return match ? match[1].trim() : "Airport Service";
  };

  const extractAirport = (message) => {
    const match = message?.match(/at :([^\.]+)\./);
    return match ? match[1].trim() : "";
  };

  const extractCustomerName = (message) => {
    const match = message?.match(/Dear ([^,]+),/);
    return match ? match[1].trim() : "";
  };

  const getServiceIcon = (serviceType) => {
    if (serviceType?.toLowerCase().includes("airport")) {
      return <FlightIcon />;
    } else if (serviceType?.toLowerCase().includes("shuttle")) {
      return <AirportShuttleIcon />;
    }
    return <AirportShuttleIcon />;
  };

  const extractDate = (message) => {
    const match = message?.match(/scheduled for ([^\.]+)\./);
    return match ? match[1].trim() : "";
  };

  const unreadCount = totalunreadcount;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          mb: 4,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          gap: 2,
        }}
      >
        <Box>
          <Box display="flex" alignItems="center" mb={1}>
            <NotificationsNoneIcon
              sx={{ mr: 1, color: "#ed6c02", fontSize: 28 }}
            />
            <Typography variant="h5" fontWeight="500">
              Notifications
            </Typography>

            {unreadCount > 0 && (
              <Chip
                label={`${unreadCount} unread`}
                className="grey-button"
                size="small"
                sx={{ ml: 2 }}
              />
            )}
          </Box>
        </Box>
      </Box>

      <Paper
        elevation={2}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : sortedNotifications && sortedNotifications.length > 0 ? (
          <List disablePadding>
            {sortedNotifications.map((notification) => {
              const bookingRef = extractBookingReference(
                notification.booking_message || notification.message || ""
              );
              const serviceType = extractServiceType(
                notification.booking_message || notification.message || ""
              );
              const airport = extractAirport(
                notification.booking_message || notification.message || ""
              );
              const customerName = extractCustomerName(
                notification.booking_message || notification.message || ""
              );
              const scheduleDate = extractDate(
                notification.booking_message || notification.message || ""
              );

              const isAirportUpdate = isAirportUpdateNotification(notification);

              return (
                <Box
                  key={notification.id || notification.admin_notification_id}
                  sx={{
                    borderLeft:
                      notification.is_readed || notification.is_read
                        ? "none"
                        : "4px solid #ed6c02",
                    borderBottom: "1px solid #f0f0f0",
                    backgroundColor:
                      notification.is_readed || notification.is_read
                        ? "white"
                        : "rgba(25, 118, 210, 0.04)",
                    transition: "background-color 0.3s",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.03)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      p: { xs: 2, md: 3 },
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      justifyContent: "space-between",
                      gap: 2,
                    }}
                    onClick={() => handleViewNotification(notification)}
                  >
                    <Box flexGrow={1}>
                      <Box display="flex" alignItems="center" mb={1}>
                        <Typography
                          variant="subtitle1"
                          fontWeight="600"
                          sx={{ ml: 1 }}
                        >
                          {isAirportUpdate ? "Airport Update" : serviceType}
                        </Typography>

                        {!(notification.is_readed || notification.is_read) && (
                          <Chip
                            label="New"
                            size="small"
                            className="orange-button"
                            sx={{ ml: 2, height: 20 }}
                          />
                        )}
                      </Box>

                      <Stack
                        direction="row"
                        spacing={2}
                        mb={1.5}
                        flexWrap="wrap"
                      >
                        {bookingRef && (
                          <Box display="flex" alignItems="center">
                            <BookmarkIcon
                              sx={{ fontSize: "0.9rem", mr: 0.5 }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: "500" }}
                            >
                              Ref: {bookingRef}
                            </Typography>
                          </Box>
                        )}

                        {airport && (
                          <Box display="flex" alignItems="center">
                            <FlightIcon
                              sx={{
                                fontSize: "0.9rem",
                                mr: 0.5,
                                color: "text.secondary",
                              }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              {airport}
                            </Typography>
                          </Box>
                        )}

                        {customerName && (
                          <Box display="flex" alignItems="center">
                            <PersonIcon
                              sx={{
                                fontSize: "0.9rem",
                                mr: 0.5,
                                color: "text.secondary",
                              }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              {customerName}
                            </Typography>
                          </Box>
                        )}

                        {scheduleDate && (
                          <Box display="flex" alignItems="center">
                            <CalendarTodayIcon
                              sx={{
                                fontSize: "0.9rem",
                                mr: 0.5,
                                color: "text.secondary",
                              }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              {scheduleDate}
                            </Typography>
                          </Box>
                        )}
                      </Stack>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                          pr: { xs: 0, sm: 8 },
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {notification.booking_message || notification.message}
                      </Typography>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <AccessTimeIcon sx={{ fontSize: "0.9rem", mr: 0.5 }} />
                        {formatDate(notification.created_at)}
                      </Typography>
                    </Box>

                    {/* Action buttons */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: { xs: "flex-start", sm: "center" },
                        alignSelf: { xs: "flex-start", sm: "center" },
                        gap: 1,
                      }}
                    >
                      {/* Show airport navigation button for airport update notifications */}
                      {isAirportUpdate && (
                        <Tooltip title="Go to Airports">
                          <Button
                            variant="contained"
                            size="small"
                            className="orange-button"
                            startIcon={<FlightIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNavigateToAirports(notification);
                            }}
                            sx={{ minWidth: "120px", mr: 1 }}
                          >
                            View Airports
                          </Button>
                        </Tooltip>
                      )}

                      <Tooltip title="View details">
                        <Button
                          variant={
                            notification.is_readed || notification.is_read
                              ? "outlined"
                              : "contained"
                          }
                          size="small"
                          className="orange-button"
                          startIcon={<VisibilityIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewNotification(notification);
                          }}
                          sx={{ minWidth: "100px" }}
                        >
                          View
                        </Button>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </List>
        ) : (
          <Box py={6} textAlign="center">
            <NotificationsNoneIcon
              sx={{
                fontSize: 60,
                color: "text.secondary",
                opacity: 0.5,
                mb: 2,
              }}
            />
            <Typography color="textSecondary" variant="h6">
              No notifications to display
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Pagination controls */}
      {!isLoading && notifications?.length > 0 && (
        <Box
          sx={{
            mt: 3,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "center", sm: "center" },
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="body2" color="textSecondary" mr={2}>
              Items per page:
            </Typography>
            <FormControl size="small" sx={{ minWidth: 80 }}>
              <Select
                value={pageSize}
                onChange={handlePageSizeChange}
                displayEmpty
                variant="outlined"
              >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {totalRecords > 0 && (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
                size="medium"
                siblingCount={1}
                boundaryCount={1}
              />
            </Box>
          )}

          <Box sx={{ display: { xs: "none", sm: "block" } }}>
            <Typography variant="body2" color="textSecondary">
              Showing {totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0} -{" "}
              {Math.min(currentPage * pageSize, totalCount)} of {totalCount}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Navigation buttons for mobile */}
      {!isLoading && totalRecords > 1 && (
        <Box
          sx={{
            mt: 2,
            display: { xs: "flex", sm: "none" },
            justifyContent: "space-between",
            px: 2,
          }}
        >
          <Button
            variant="outlined"
            disabled={currentPage <= 1}
            onClick={() => handlePageChange(null, currentPage - 1)}
            startIcon={<NavigateBeforeIcon />}
            className="orange-button"
            size="small"
          >
            Previous
          </Button>

          <Typography variant="body2" sx={{ alignSelf: "center" }}>
            Page {currentPage} of {totalRecords}
          </Typography>

          <Button
            variant="outlined"
            disabled={currentPage >= totalRecords}
            onClick={() => handlePageChange(null, currentPage + 1)}
            endIcon={<NavigateNextIcon />}
            className="orange-button"
            size="small"
          >
            Next
          </Button>
        </Box>
      )}

      {/* Detail Drawer for showing full notification details */}
      <Drawer
        anchor="right"
        open={isDetailDrawerOpen}
        onClose={handleCloseDetailDrawer}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: "550px" },
            p: 0,
            boxShadow: "-4px 0 12px rgba(0,0,0,0.08)",
          },
        }}
      >
        {selectedNotification && (
          <>
            <Box
              sx={{
                p: 2,
                display: "flex",
                alignItems: "center",
                borderBottom: "1px solid #f0f0f0",
              }}
            >
              <IconButton
                edge="start"
                color="inherit"
                onClick={handleCloseDetailDrawer}
                aria-label="close"
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6" sx={{ ml: 2, flexGrow: 1 }}>
                Notification Details
              </Typography>
              {(selectedNotification.is_readed ||
                selectedNotification.is_read) && (
                <Tooltip title="Read">
                  <CheckCircleOutlineIcon color="primary" />
                </Tooltip>
              )}
            </Box>

            <Box sx={{ px: 3, py: 4 }}>
              {/* Add airport navigation button in drawer for airport updates */}
              {isAirportUpdateNotification(selectedNotification) && (
                <Box sx={{ mb: 3 }}>
                  <Button
                    variant="contained"
                    size="large"
                    className="orange-button"
                    startIcon={<FlightIcon />}
                    onClick={() => {
                      handleNavigateToAirports(selectedNotification);
                      handleCloseDetailDrawer();
                    }}
                    fullWidth
                  >
                    Go to Airports
                  </Button>
                </Box>
              )}

              <Card elevation={0} sx={{ mb: 4, border: "1px solid #f0f0f0" }}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    {isAirportUpdateNotification(selectedNotification) ? (
                      <FlightIcon />
                    ) : (
                      getServiceIcon(
                        extractServiceType(
                          selectedNotification.booking_message ||
                            selectedNotification.message ||
                            ""
                        )
                      )
                    )}
                    <Typography variant="h6" sx={{ ml: 1 }}>
                      {isAirportUpdateNotification(selectedNotification)
                        ? "Airport Update"
                        : extractServiceType(
                            selectedNotification.booking_message ||
                              selectedNotification.message ||
                              ""
                          )}
                    </Typography>
                  </Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {formatDate(selectedNotification.created_at)}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={2}>
                    {extractBookingReference(
                      selectedNotification.booking_message ||
                        selectedNotification.message ||
                        ""
                    ) && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Booking Reference
                        </Typography>
                        <Typography
                          variant="body1"
                          color="primary"
                          fontWeight="500"
                        >
                          {extractBookingReference(
                            selectedNotification.booking_message ||
                              selectedNotification.message ||
                              ""
                          )}
                        </Typography>
                      </Grid>
                    )}

                    {extractCustomerName(
                      selectedNotification.booking_message ||
                        selectedNotification.message ||
                        ""
                    ) && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Customer
                        </Typography>
                        <Typography variant="body1">
                          {extractCustomerName(
                            selectedNotification.booking_message ||
                              selectedNotification.message ||
                              ""
                          )}
                        </Typography>
                      </Grid>
                    )}

                    {extractAirport(
                      selectedNotification.booking_message ||
                        selectedNotification.message ||
                        ""
                    ) && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Location
                        </Typography>
                        <Typography variant="body1">
                          {extractAirport(
                            selectedNotification.booking_message ||
                              selectedNotification.message ||
                              ""
                          )}
                        </Typography>
                      </Grid>
                    )}

                    {extractDate(
                      selectedNotification.booking_message ||
                        selectedNotification.message ||
                        ""
                    ) && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Date
                        </Typography>
                        <Typography variant="body1">
                          {extractDate(
                            selectedNotification.booking_message ||
                              selectedNotification.message ||
                              ""
                          )}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>

              <Typography
                variant="h6"
                sx={{ mb: 2, display: "flex", alignItems: "center" }}
              >
                <EmailIcon sx={{ mr: 1 }} /> Message
              </Typography>

              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  backgroundColor: "#f9f9f9",
                  borderRadius: 2,
                  border: "1px solid #f0f0f0",
                  whiteSpace: "pre-line",
                }}
              >
                <Typography variant="body1">
                  {selectedNotification.booking_message ||
                    selectedNotification.message}
                </Typography>
              </Paper>
            </Box>
          </>
        )}
      </Drawer>
    </Box>
  );
};

export default NotificationsPage;